import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../config/api';
import NewConversationModal from '../components/NewConversationModal';
import BuyUnitsModal from '../components/BuyUnitsModal';

// Inject custom fonts
const customStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=DM+Sans:wght@400;500&display=swap');
  
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  @keyframes orbGlow {
    0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.4); }
    50% { box-shadow: 0 0 40px rgba(102, 126, 234, 0.6); }
  }
`;

// Collaboration Templates
const TEMPLATES = [
  { id: 'strategy', icon: '🎯', title: 'Strategy Session', desc: 'Business decisions & planning', ais: ['claude', 'gpt'], color: '#667eea' },
  { id: 'writing', icon: '✍️', title: 'Writing Collab', desc: 'Content creation & editing', ais: ['claude', 'gpt', 'grok'], color: '#11998e' },
  { id: 'startup', icon: '🚀', title: 'Startup Brainstorm', desc: 'Ideas & market validation', ais: ['claude', 'gpt', 'deepseek'], color: '#eb3349' },
  { id: 'creative', icon: '🎨', title: 'Branding & Creative', desc: 'Identity & messaging', ais: ['claude', 'gpt'], color: '#f093fb' },
  { id: 'marketing', icon: '📣', title: 'Marketing Campaign', desc: 'Campaigns & copy', ais: ['gpt', 'grok'], color: '#ff6b6b' },
  { id: 'technical', icon: '⚙️', title: 'Technical Review', desc: 'Architecture & code', ais: ['claude', 'deepseek'], color: '#4ecdc4' },
  { id: 'research', icon: '🔬', title: 'Deep Research', desc: 'Analysis & investigation', ais: ['claude', 'deepseek'], color: '#a855f7' },
  { id: 'productivity', icon: '📋', title: 'Productivity', desc: 'Planning & workflows', ais: ['claude', 'gpt'], color: '#06b6d4' }
];

const AI_INFO = {
  claude: { icon: '🧠', name: 'Claude', color: '#667eea', desc: 'Anthropic' },
  gpt: { icon: '✨', name: 'GPT-4o', color: '#11998e', desc: 'OpenAI' },
  grok: { icon: '⚡', name: 'Grok', color: '#eb3349', desc: 'xAI' },
  deepseek: { icon: '🔮', name: 'DeepSeek', color: '#f093fb', desc: 'DeepSeek' }
};

// Sidebar Navigation Component
const Sidebar = ({ activeItem, onNavigate, user, unitsBalance = 0, onBuyUnits, onLogout }) => {
  const navItems = [
    { id: 'dashboard', icon: '🏠', label: 'Dashboard' },
    { id: 'conversations', icon: '💬', label: 'Conversations' },
    { id: 'buy-units', icon: '🪙', label: 'Buy Units' },
    { id: 'settings', icon: '⚙️', label: 'Settings' }
  ];

  return (
    <div style={{
      width: '240px',
      background: 'rgba(255,255,255,0.02)',
      borderRight: '1px solid rgba(255,255,255,0.06)',
      display: 'flex',
      flexDirection: 'column',
      padding: '24px 16px',
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0
    }}>
      {/* Logo */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '40px',
        paddingLeft: '8px'
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px'
        }}>
          ⚡
        </div>
        <span style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '18px',
          fontWeight: '600',
          color: 'white'
        }}>
          BrainForge
        </span>
      </div>

      {/* Nav Items */}
      <nav style={{ flex: 1 }}>
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: activeItem === item.id ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
              border: 'none',
              borderRadius: '10px',
              color: activeItem === item.id ? '#667eea' : 'rgba(255,255,255,0.6)',
              fontSize: '14px',
              cursor: 'pointer',
              marginBottom: '4px',
              transition: 'all 0.2s'
            }}
          >
            <span style={{ fontSize: '18px' }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </nav>

      {/* User Section */}
      <div style={{
        padding: '16px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px'
          }}>
            {user?.fullName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: 'white',
              fontSize: '13px',
              fontWeight: '500',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.fullName || user?.email?.split('@')[0]}
            </div>
            <div style={{ color: '#22c55e', fontSize: '11px', fontWeight: '600' }}>
              🪙 {unitsBalance} units
            </div>
          </div>
        </div>
        
        {/* Buy Units Button */}
        <button
          onClick={onBuyUnits}
          style={{
            width: '100%',
            padding: '10px',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))',
            border: '1px solid rgba(102, 126, 234, 0.4)',
            borderRadius: '8px',
            color: '#667eea',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          🛒 Buy Units <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: '400' }}>• Save up to 60%</span>
        </button>
        
        {/* Logout Button */}
        <button
          onClick={onLogout}
          style={{
            width: '100%',
            padding: '8px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          🚪 Sign Out
        </button>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, subtext, color }) => (
  <div style={{
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '16px',
    padding: '20px',
    flex: 1
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '12px'
    }}>
      <span style={{ fontSize: '20px' }}>{icon}</span>
      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{label}</span>
    </div>
    <div style={{
      fontSize: '32px',
      fontWeight: '700',
      color: 'white',
      fontFamily: "'Outfit', sans-serif"
    }}>
      {value}
    </div>
    {subtext && (
      <div style={{ color: color || '#22c55e', fontSize: '12px', marginTop: '4px' }}>
        {subtext}
      </div>
    )}
  </div>
);

export default function Dashboard() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [unitsBalance, setUnitsBalance] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeNav, setActiveNav] = useState('dashboard');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const displayName = user?.fullName || user?.full_name || user?.name || user?.email?.split('@')[0] || 'there';
  const configuredAIs = ['claude', 'gpt', 'grok', 'deepseek']; // All 4 AIs always available
  const totalConversations = conversations.length;
  const thisWeekConvos = conversations.filter(c => {
    const date = new Date(c.created_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date > weekAgo;
  }).length;

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = customStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    if (searchParams.get('purchase') === 'success') {
      loadUnitsBalance();
      alert('🎉 Units purchased successfully!');
      window.history.replaceState({}, '', '/dashboard');
    }
    if (searchParams.get('showPurchase') === 'true') {
      setShowSubscription(true);
      window.history.replaceState({}, '', '/dashboard');
    }
    loadConversations();
    loadUnitsBalance();
  }, []);

  const loadUnitsBalance = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(apiUrl('/api/units/balance'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUnitsBalance(data.balance || 0);
    } catch (e) {
      console.error('Failed to load units');
    }
  };

  const loadConversations = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(apiUrl('/api/conversations'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch (e) {
      console.error('Failed to load conversations');
    }
    setLoading(false);
  };

  const handleNavigation = (id) => {
    setActiveNav(id);
    if (id === 'settings') navigate('/settings');
    if (id === 'buy-units') setShowSubscription(true);
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setShowNewConvo(true);
  };

  const handleNewConversation = () => {
    setSelectedTemplate(null);
    setShowNewConvo(true);
  };

  const handleConversationCreated = (conversation) => {
    navigate(`/conversation/${conversation.id}`);
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0f1c 0%, #0d1525 100%)',
      fontFamily: "'DM Sans', sans-serif",
      display: 'flex'
    }}>
      {/* Sidebar */}
      <Sidebar activeItem={activeNav} onNavigate={handleNavigation} user={user} unitsBalance={unitsBalance} onBuyUnits={() => setShowSubscription(true)} onLogout={logout} />

      {/* Main Content */}
      <div style={{
        flex: 1,
        marginLeft: '240px',
        padding: '32px 40px',
        maxWidth: '1400px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '32px',
              fontWeight: '700',
              color: 'white',
              marginBottom: '8px'
            }}>
              Welcome back, {displayName}! 👋
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px' }}>
              Your BrainForge AI is ready to collaborate
            </p>
          </div>
          <button
            onClick={handleNewConversation}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.2s'
            }}
          >
            <span style={{ fontSize: '18px' }}>✨</span>
            New Conversation
          </button>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard
            icon="💬"
            label="Total Conversations"
            value={totalConversations}
            subtext={thisWeekConvos > 0 ? `+${thisWeekConvos} this week` : null}
          />
          <StatCard
            icon="🤖"
            label="Active AIs"
            value={configuredAIs.length}
            subtext={configuredAIs.length < 4 ? `${4 - configuredAIs.length} more available` : 'All configured'}
            color={configuredAIs.length < 2 ? '#f59e0b' : '#22c55e'}
          />
          <StatCard
            icon="⚡"
            label="Status"
            value={`${unitsBalance} units`}
            subtext='Available balance'
          />
        </div>

        {/* AI Team Status */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px'
        }}>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '18px',
            fontWeight: '600',
            color: 'white',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span>🤖</span> Your AI Team
          </h2>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {Object.entries(AI_INFO).map(([key, ai]) => {
              const isConfigured = true; // All AIs always available
              return (
                <div
                  key={key}
                  style={{
                    flex: '1 1 200px',
                    background: isConfigured ? `${ai.color}15` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${isConfigured ? `${ai.color}40` : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    opacity: isConfigured ? 1 : 0.5
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{ai.icon}</span>
                  <div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>{ai.name}</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>{ai.desc}</div>
                  </div>
                  <div style={{
                    marginLeft: 'auto',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: isConfigured ? '#22c55e' : 'rgba(255,255,255,0.2)'
                  }} />
                </div>
              );
            })}
          </div>
          {configuredAIs.length < 2 && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>⚠️</span>
              <span style={{ color: '#fbbf24', fontSize: '13px' }}>
                Add at least 2 API keys to start collaborating.{' '}
                <Link to="/settings" style={{ color: '#667eea', textDecoration: 'underline' }}>
                  Add keys →
                </Link>
              </span>
            </div>
          )}
        </div>

        {/* Templates Grid */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '18px',
            fontWeight: '600',
            color: 'white',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span>🎯</span> Quick Start Templates
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '16px'
          }}>
            {TEMPLATES.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateClick(template)}
                onMouseEnter={() => setHoveredCard(template.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: hoveredCard === template.id ? `${template.color}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${hoveredCard === template.id ? `${template.color}40` : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '16px',
                  padding: '20px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  transform: hoveredCard === template.id ? 'translateY(-2px)' : 'none'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: `${template.color}20`,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  marginBottom: '12px'
                }}>
                  {template.icon}
                </div>
                <h3 style={{ color: 'white', fontSize: '15px', fontWeight: '600', marginBottom: '6px' }}>
                  {template.title}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '12px' }}>
                  {template.desc}
                </p>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {template.ais.slice(0, 3).map((ai) => (
                    <span key={ai} style={{
                      fontSize: '14px',
                      padding: '4px 8px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '6px'
                    }}>
                      {AI_INFO[ai]?.icon}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Conversations */}
        <div>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '18px',
            fontWeight: '600',
            color: 'white',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span>📜</span> Recent Conversations
          </h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
              Loading...
            </div>
          ) : conversations.length === 0 ? (
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '16px',
              padding: '60px 40px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
              <h3 style={{ color: 'white', marginBottom: '8px' }}>No conversations yet</h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '20px' }}>
                Start your first AI collaboration session
              </p>
              <button
                onClick={handleNewConversation}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Start Conversation
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {conversations.slice(0, 5).map((convo) => (
                <div
                  key={convo.id}
                  onClick={() => navigate(`/conversation/${convo.id}`)}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #667eea20, #764ba220)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px'
                  }}>
                    💬
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {convo.initial_task?.slice(0, 60)}...
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '4px' }}>
                      {convo.rounds_completed} rounds • {getTimeAgo(convo.updated_at || convo.created_at)}
                    </div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px' }}>→</div>
                </div>
              ))}
              {conversations.length > 5 && (
                <button
                  onClick={() => setActiveNav('conversations')}
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  View all {conversations.length} conversations →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '60px',
          paddingTop: '24px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
            © 2025 BrainForge. All rights reserved.
          </div>
          <div style={{ display: 'flex', gap: '20px' }}>
            {['Terms', 'Privacy', 'Contact'].map((item) => (
              <Link
                key={item}
                to={`/${item.toLowerCase()}`}
                style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', textDecoration: 'none' }}
              >
                {item}
              </Link>
            ))}
            <button
              onClick={logout}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showNewConvo && (
        <NewConversationModal
          onClose={() => setShowNewConvo(false)}
          onCreated={handleConversationCreated}
          template={selectedTemplate}
          currentBalance={unitsBalance}
        />
      )}
      {showSubscription && (
        <BuyUnitsModal
          onClose={() => { setShowSubscription(false); loadUnitsBalance(); }}
          currentBalance={unitsBalance}
        />
      )}
    </div>
  );
}
